import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import api, { formatMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, Fire } from "@phosphor-icons/react";

function PlanCard({ plan, highlight, ctaLabel, ctaHref, currentLevel }) {
  const founding = plan.founding_active;
  const displayPrice = founding ? plan.founding_price : plan.yearly_price;
  const isCurrent = currentLevel === plan.level;

  return (
    <div className={`surface p-8 flex flex-col ${highlight ? "border-[#00D4FF]" : ""}`} data-testid={`plan-card-level-${plan.level}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[11px] text-[#00D4FF] tracking-widest uppercase">LEVEL {plan.level}</div>
          <h3 className="font-chivo font-black text-2xl text-white uppercase tracking-tight mt-1">{plan.name}</h3>
        </div>
        {highlight && (
          <span className="tag tag-active flex items-center gap-1">
            <Fire size={10} weight="fill" /> POPULAR
          </span>
        )}
      </div>

      <div className="flex items-baseline gap-2 mb-1">
        <span className="font-chivo font-black text-5xl text-white tracking-tighter">${displayPrice.toLocaleString()}</span>
        <span className="font-mono text-sm text-[#52525B]">/YEAR</span>
      </div>
      {founding ? (
        <div className="font-mono text-[11px] text-[#00D4FF] tracking-widest uppercase mt-1">
          FOUNDING OFFER · {plan.founding_remaining} / {plan.founding_slots} SPOTS LEFT
        </div>
      ) : (
        <div className="font-mono text-[11px] text-[#52525B] tracking-widest uppercase mt-1">ANNUAL · CANCEL ANYTIME</div>
      )}
      {founding && (
        <div className="font-mono text-xs text-[#52525B] mt-1">
          Standard price returns to ${plan.yearly_price.toLocaleString()}/yr after founding slots fill.
        </div>
      )}

      <ul className="mt-6 space-y-3 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-3 font-plex text-sm text-white">
            <CheckCircle size={18} weight="fill" className="text-[#00D4FF] shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8">
        {isCurrent ? (
          <div className="tag tag-active w-full text-center py-3" data-testid={`plan-${plan.level}-current`}>
            YOUR CURRENT PLAN
          </div>
        ) : (
          <Link to={ctaHref} className={highlight ? "btn-primary w-full" : "btn-secondary w-full"} data-testid={`plan-${plan.level}-cta`}>
            {ctaLabel}
          </Link>
        )}
      </div>
    </div>
  );
}

export default function Pricing() {
  const { user, isMember } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/plans");
        setPlans(data);
      } catch (err) {
        console.error("Failed to load plans:", err);
      }
      setLoading(false);
    })();
  }, []);

  const currentLevel = user?.level || 0;
  const isAuthed = !!user && user !== false;

  const ctaFor = (planLevel) => {
    if (!isAuthed) {
      return { label: planLevel === 2 ? "Join as Network Pro" : "Join Now", href: `/register` };
    }
    if (currentLevel >= planLevel) {
      return { label: "Already a Member", href: "/account" };
    }
    if (currentLevel === 1 && planLevel === 2) {
      return { label: "Upgrade to Network Pro", href: `/billing?level=2` };
    }
    return { label: planLevel === 2 ? "Choose Network Pro" : "Choose Level 1", href: `/billing?level=${planLevel}` };
  };

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16">
        <div className="text-center mb-12">
          <div className="section-label mb-3">— MEMBERSHIP</div>
          <h1 className="font-chivo font-black text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tighter leading-none" data-testid="pricing-headline">Choose your level.</h1>
          <p className="font-plex text-[#9CA3AF] mt-6 max-w-xl mx-auto">
            Both plans unlock member pricing on pellets, machines and accessories, plus training, downloads and private support. Level 2 adds a public listing on our customer map — no commission, direct leads.
          </p>
        </div>

        {loading ? (
          <div className="font-mono text-xs text-[#52525B] text-center">[LOADING PLANS]</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {plans.map((p) => {
              const cta = ctaFor(p.level);
              return (
                <PlanCard
                  key={p.id}
                  plan={p}
                  highlight={p.level === 2}
                  ctaLabel={cta.label}
                  ctaHref={cta.href}
                  currentLevel={currentLevel}
                />
              );
            })}
          </div>
        )}

        <div className="mt-16 text-center">
          <div className="section-label mb-3">— NEED TO TALK?</div>
          <p className="font-plex text-sm text-[#9CA3AF]">
            Questions about Level 2 or founding spots? Email{" "}
            <a href="mailto:support@dryiceconnect.com" className="text-[#00D4FF] hover:underline">support@dryiceconnect.com</a>
          </p>
        </div>
      </div>
    </Layout>
  );
}
